/**
 * Collapse a burst of pause/resume decisions into two requests.
 *
 * lastN and the audio cap both decide about the WHOLE room at once and then
 * issued one request per consumer. A page flip in a 300-peer meeting was ~16
 * resumes and ~16 pauses as 32 separate round trips; the audio cap engaging is
 * ~270 in one go. The decision was always a batch — only the transport was not.
 *
 * Two distinct problems, and the order they are solved in matters:
 *
 *  - DEBOUNCE collapses a burst of DECISIONS. A grid repaint, a page flip and
 *    an active-speaker push can land in the same few hundred milliseconds, and
 *    without this we send a correct batch and then immediately send its
 *    opposite.
 *  - BULK collapses one decision's N requests into one.
 *
 * Debounce first, bulk second. The other way round you efficiently send
 * batches you are about to contradict.
 *
 * WHAT THIS DELIBERATELY DOES NOT TOUCH: the `lastNVisible` / `audioAudible`
 * latches. Those are `decideLastN`'s memory of what it has already decided, and
 * they are written before a request is issued on purpose. Moving them here
 * would be the obvious refactor and it is a trap: a coalesced reversal would
 * see "already in that state" and send nothing, leaving the server out of sync
 * with no error anywhere. So this keeps its OWN record — `sent`, what the wire
 * has actually carried — and the two never have to agree about timing.
 *
 * That record is what makes A -> B -> A cost nothing: the pending state ends
 * where `sent` already is, so the flush drops it entirely.
 */

/** How long a decision waits for more decisions. Trailing edge. */
export const BATCH_WINDOW_MS = 200

export type BatchSend = (consumerIds: string[], paused: boolean) => Promise<BatchResult>

export type BatchResult = {
  /** Ids the server did not have — stale, and worth forgetting locally. */
  missing?: string[]
}

type Pending = {
  /** consumerId -> the state most recently decided for it. Later wins. */
  want: Map<string, boolean>
  timer: ReturnType<typeof setTimeout> | null
}

/**
 * One batcher per meeting. Not a module singleton: two meetings in one tab
 * would share the pending map and flush each other's consumer ids at a server
 * that has never heard of them.
 */
export function createConsumerBatch(send: BatchSend, windowMs: number = BATCH_WINDOW_MS) {
  const pending: Pending = { want: new Map(), timer: null }
  /** What the server has actually been told, as far as we know. */
  const sent = new Map<string, boolean>()
  /** Called with the ids whose request failed, so a caller can unlatch them. */
  let onFailed: ((ids: string[], paused: boolean) => void) | null = null

  function schedule() {
    if (pending.timer) return
    pending.timer = setTimeout(() => { pending.timer = null; void flush() }, windowMs)
  }

  async function flush(): Promise<void> {
    if (pending.timer) { clearTimeout(pending.timer); pending.timer = null }
    if (pending.want.size === 0) return

    const resume: string[] = []
    const pause: string[] = []
    for (const [id, wanted] of pending.want) {
      // Already there as far as the wire is concerned. This is the whole point:
      // a tile that went away and came back inside the window costs nothing.
      if (sent.get(id) === wanted) continue
      ;(wanted ? resume : pause).push(id)
    }
    pending.want.clear()
    if (resume.length === 0 && pause.length === 0) return

    // Both are issued together. They address disjoint sets of consumers, so
    // there is no ordering between them to preserve, and serialising them would
    // put a whole round trip between a tile being hidden and another appearing.
    await Promise.all([
      dispatch(resume, false),
      dispatch(pause, true),
    ])
  }

  async function dispatch(ids: string[], paused: boolean): Promise<void> {
    if (ids.length === 0) return
    // Recorded BEFORE the await. A second flush landing while this is in flight
    // must not re-send the same ids — and if the request fails they are removed
    // again below, so the only cost of being early is a retry we would have
    // made anyway.
    for (const id of ids) sent.set(id, !paused)
    try {
      const r = await send(ids, paused)
      // The server did not have these. Forget them rather than carrying a
      // belief about a consumer that no longer exists.
      for (const id of r?.missing ?? []) sent.delete(id)
    } catch {
      for (const id of ids) sent.delete(id)
      onFailed?.(ids, paused)
    }
  }

  return {
    /** Record a decision. Nothing is sent until the window closes. */
    set(consumerId: string, wanted: boolean): void {
      pending.want.set(consumerId, wanted)
      schedule()
    },
    /** Record many at once — the shape `decideLastN` already returns. */
    setMany(resume: readonly string[], pause: readonly string[]): void {
      for (const id of resume) pending.want.set(id, true)
      for (const id of pause) pending.want.set(id, false)
      if (resume.length || pause.length) schedule()
    },
    /**
     * Send now, without waiting for the window.
     *
     * For the cases where a round trip of latency is worse than a wasted
     * request — the user pressing "pause all incoming video" should not sit
     * there for 200ms watching nothing happen.
     */
    flushNow(): Promise<void> {
      return flush()
    },
    /** Told when a request failed, so the caller can clear its own latch. */
    onFailure(fn: (ids: string[], paused: boolean) => void): void {
      onFailed = fn
    },
    /**
     * A consumer is gone — drop every belief about it.
     *
     * Without this a consumer id that is reused (or simply never cleaned up)
     * keeps a stale entry in `sent` for the life of the meeting, and the first
     * decision about its replacement is dropped as redundant.
     */
    forget(consumerId: string): void {
      pending.want.delete(consumerId)
      sent.delete(consumerId)
    },
    /** Teardown. A pending timer must not fire into a closed meeting. */
    close(): void {
      if (pending.timer) { clearTimeout(pending.timer); pending.timer = null }
      pending.want.clear()
      sent.clear()
      onFailed = null
    },
    /** Exposed for tests — what the wire is believed to have carried. */
    __sent: sent,
  }
}
